import { Injectable } from '@angular/core';
import {
  CartridgeWithSelectableRoles,
  VariantRoleAssignmentLimit,
  VariantRoleAssignmentLimitResponse,
} from '@markant-platform/authorizer/model';
import { CallbackFunctionUpdateSlots, RoleLimitsService } from './role-limits.service';

@Injectable({
  providedIn: 'root',
})
export class GroupRoleLimitsStrategyService implements RoleLimitsService {
  private totalUsers = 0;

  setTotalUsers(total: number): void {
    this.totalUsers = total;
  }

  buildLimit(
    cartridgeWithSelectableRoles: CartridgeWithSelectableRoles,
    roleLimits?: VariantRoleAssignmentLimitResponse[]
  ): VariantRoleAssignmentLimit {
    if (!roleLimits?.length) {
      return { allowsAssignment: true };
    }

    const limit = roleLimits.find(roleLimit => roleLimit.variantId === cartridgeWithSelectableRoles.id);

    if (limit) {
      const { availableServiceRoleAssignmentsLeft, roleAssignmentLimit, usedSlotsByGroup } = limit;
      const hasAnyRoleSelected = cartridgeWithSelectableRoles.roles?.some(({ selected }) => selected ?? false);

      return {
        availableSlots: availableServiceRoleAssignmentsLeft,
        limit: roleAssignmentLimit,
        allowsAssignment: this.allowsAssignment(
          availableServiceRoleAssignmentsLeft,
          usedSlotsByGroup,
          hasAnyRoleSelected
        ),
        usedSlotsByGroup,
      };
    }

    return { allowsAssignment: true };
  }

  incrementLimits(limits?: VariantRoleAssignmentLimit): VariantRoleAssignmentLimit | undefined {
    return this.recalculateLimits(this.incrementSlots, limits);
  }

  decrementLimits(limits?: VariantRoleAssignmentLimit): VariantRoleAssignmentLimit | undefined {
    return this.recalculateLimits(this.decrementSlots, limits);
  }

  private recalculateLimits(
    updateSlotsFn: CallbackFunctionUpdateSlots,
    limits?: VariantRoleAssignmentLimit
  ): VariantRoleAssignmentLimit | undefined {
    if (!limits) {
      return undefined;
    }

    const slotsInUse = limits.usedSlotsByGroup ?? 0;

    // we need to strictly check the null value
    if (limits.availableSlots == null) {
      return limits;
    }

    const availableSlots = updateSlotsFn(limits.availableSlots, slotsInUse, this.totalUsers);

    return {
      ...limits,
      availableSlots,
    };
  }

  private incrementSlots(availableSlots: number, slotsInUse: number, totalUsers: number): number {
    return availableSlots - slotsInUse + totalUsers;
  }

  private decrementSlots(availableSlots: number, slotsInUse: number, totalUsers: number): number {
    return availableSlots + slotsInUse - totalUsers;
  }

  private allowsAssignment(availableSlots?: number, usedSlotsByGroup = 0, hasAnyRoleSelected = false): boolean {
    return availableSlots == null || availableSlots + usedSlotsByGroup >= this.totalUsers || hasAnyRoleSelected;
  }
}
